import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { UnsupportedFileFeedbackFields } from '@/components/project/UnsupportedFileFeedbackFields'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/context/AuthContext'
import { useTranslation } from '@/hooks/useTranslation'
import { isSentryUserFeedbackAvailable } from '@/services/sentry'
import {
	fileTypeDescriptionFromContext,
	submitUnsupportedFileUploadFeedbackFromForm,
} from '@/services/unsupportedFileUploadFeedback'
import type { UnsupportedFileUploadContext } from '@/utils/uploadErrors'

interface UnsupportedFileUploadFeedbackModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	context: UnsupportedFileUploadContext | null
}

export function UnsupportedFileUploadFeedbackModal({
	open,
	onOpenChange,
	context,
}: UnsupportedFileUploadFeedbackModalProps) {
	const { t } = useTranslation()
	const { user } = useAuth()
	const [fileTypeDescription, setFileTypeDescription] = useState('')
	const [willingToShare, setWillingToShare] = useState(false)
	const [useCase, setUseCase] = useState('')
	const [shareConsentError, setShareConsentError] = useState(false)
	const [useCaseError, setUseCaseError] = useState(false)

	useEffect(() => {
		if (!open || !context) {
			return
		}

		setFileTypeDescription(
			fileTypeDescriptionFromContext(context, t('unsupportedFileUploadFeedback.unknownFileType'))
		)
		setWillingToShare(false)
		setUseCase('')
		setShareConsentError(false)
		setUseCaseError(false)
	}, [context, open, t])

	if (!isSentryUserFeedbackAvailable()) {
		return null
	}

	const handleSubmit = () => {
		if (!context) {
			return
		}

		const trimmedFileType = fileTypeDescription.trim()
		const trimmedUseCase = useCase.trim()

		if (!trimmedUseCase) {
			setUseCaseError(true)
			return
		}

		if (!trimmedFileType) {
			toast.error(t('unsupportedFileUploadFeedback.validationError'))
			return
		}

		if (!willingToShare) {
			setShareConsentError(true)
			return
		}

		const eventId = submitUnsupportedFileUploadFeedbackFromForm(
			context,
			{
				fileTypeDescription: trimmedFileType,
				useCase: trimmedUseCase,
				willingToShare,
			},
			user
		)

		if (!eventId) {
			toast.error(t('unsupportedFileUploadFeedback.submitError'))
			return
		}

		toast.success(t('unsupportedFileUploadFeedback.submitSuccess'))
		onOpenChange(false)
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{t('unsupportedFileUploadFeedback.title')}</DialogTitle>
					<DialogDescription>{t('unsupportedFileUploadFeedback.description')}</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="unsupported-upload-file-type">
							{t('unsupportedFileUploadFeedback.fileTypeLabel')}
						</Label>
						<Input
							id="unsupported-upload-file-type"
							value={fileTypeDescription}
							onChange={(e) => setFileTypeDescription(e.target.value)}
							placeholder={t('unsupportedFileUploadFeedback.fileTypePlaceholder')}
							maxLength={200}
						/>
					</div>

					<UnsupportedFileFeedbackFields
						useCase={useCase}
						onUseCaseChange={(value) => {
							setUseCase(value)
							if (value.trim()) setUseCaseError(false)
						}}
						useCaseError={useCaseError}
						willingToShare={willingToShare}
						onWillingToShareChange={(agreed) => {
							setWillingToShare(agreed)
							if (agreed) setShareConsentError(false)
						}}
						shareConsentError={shareConsentError}
					/>
				</div>

				<DialogFooter className="gap-2 sm:gap-0">
					<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
						{t('unsupportedFileUploadFeedback.skip')}
					</Button>
					<Button type="button" onClick={handleSubmit}>
						{t('unsupportedFileUploadFeedback.submit')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
